import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import Marquee from "react-fast-marquee";
import { SITE } from "../data/site";

const CLIENTS = SITE.clients;

function Star() {
  return (
    <span
      aria-hidden
      className="mx-8 md:mx-14 text-[var(--gold)] opacity-70 text-xl md:text-2xl align-middle"
    >
      ✦
    </span>
  );
}

function NameMarqueeRow({ items, direction = "left", speed = 30, italic = false }) {
  return (
    <Marquee gradient={false} speed={speed} pauseOnHover direction={direction} className="py-2">
      {items.map((c, i) => (
        <span key={`${c.name}-${i}`} className="inline-flex items-center">
          <span
            className={`font-display text-5xl md:text-7xl leading-none whitespace-nowrap ${
              italic ? "italic font-light text-[var(--silver)]" : "text-[var(--ink)]"
            }`}
          >
            {c.name}
          </span>
          <Star />
        </span>
      ))}
    </Marquee>
  );
}

function LogoMarqueeRow({ items, direction = "left", speed = 22 }) {
  return (
    <Marquee gradient={false} speed={speed} pauseOnHover direction={direction} className="py-6">
      {items.concat(items).map((c, i) => (
        <div
          key={`${c.name}-${i}`}
          className="mx-6 md:mx-10 h-24 md:h-32 w-48 md:w-64 bg-white rounded-sm flex items-center justify-center px-6 md:px-8 shadow-[0_10px_40px_-15px_rgba(0,0,0,0.6)]"
          title={c.name}
        >
          <img
            src={c.logo}
            alt={c.name}
            className="max-h-full max-w-full object-contain"
            loading="lazy"
            draggable={false}
          />
        </div>
      ))}
    </Marquee>
  );
}

function LogoTile({ c, i }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-10%" }}
      transition={{ delay: (i % 10) * 0.03, duration: 0.5 }}
      className="group relative bg-[#dcdcdc] aspect-[3/2] flex items-center justify-center p-6 md:p-8 overflow-hidden"
      data-testid={`client-${c.name.replace(/[^a-z0-9]/gi, "").toLowerCase()}`}
    >
      <img
        src={c.logo}
        alt={c.name}
        className="max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-105"
        loading="lazy"
        draggable={false}
      />
      <span className="absolute top-2 left-2 font-mono text-[9px] text-black/50">
        {String(i + 1).padStart(2, "0")}
      </span>
      <span className="absolute bottom-2 right-3 text-[9px] tracking-[0.28em] uppercase text-black/45 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
        {c.name}
      </span>
    </motion.div>
  );
}

function NameTile({ c, i }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-10%" }}
      transition={{ delay: (i % 10) * 0.03, duration: 0.5 }}
      className="group relative bg-[var(--bg-2)] aspect-[3/2] flex items-center justify-center p-4 overflow-hidden"
      data-testid={`client-${c.name.replace(/[^a-z0-9]/gi, "").toLowerCase()}`}
    >
      <span className="absolute inset-0 bg-gradient-to-br from-[var(--gold)]/0 to-[var(--gold)]/0 group-hover:from-[var(--gold)]/10 transition-colors duration-500" />
      <span className="relative font-display text-[16px] md:text-[19px] text-center leading-tight text-[var(--ink-dim)] group-hover:text-[var(--ink)] transition-colors duration-500">
        {c.name}
      </span>
      <span className="absolute top-2 left-2 font-mono text-[9px] text-[var(--ink-mute)]">
        {String(i + 1).padStart(2, "0")}
      </span>
    </motion.div>
  );
}

export default function Clients() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-15%" });

  // split logos into two rows for dual-direction marquee
  const rowA = CLIENTS.filter((_, i) => i % 2 === 0);
  const rowB = CLIENTS.filter((_, i) => i % 2 === 1);

  return (
    <section
      id="clients"
      ref={ref}
      className="relative py-24 md:py-32 overflow-hidden"
      data-testid="clients-section"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 items-end mb-14 md:mb-20">
          <div className="col-span-12 md:col-span-7">
            <div className="flex items-center gap-4 text-[11px] tracking-[0.32em] uppercase text-[var(--ink-dim)] mb-6">
              <span className="w-10 h-px bg-[var(--gold)]" />
              <span className="text-[var(--gold)] font-mono">04</span>
              <span>Associated Companies & Brands</span>
            </div>
            <h2 className="font-display text-5xl md:text-7xl leading-[0.98] tracking-[-0.02em]">
              A roster earned <span className="italic text-[var(--gold)] font-light">quietly</span>,
              over decades.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 md:col-start-9">
            <p className="text-[15px] md:text-[16px] leading-[1.6] text-[var(--ink-dim)]">
              From category shapers like Marlboro and Gillette to modern
              challengers like WheelsEye and Mentor Cart. Orentis has quietly
              partnered with {CLIENTS.length}+ brands across FMCG, personal
              care, financial services, mobility, and incubators.
            </p>
          </div>
        </div>
      </div>

      {/* Dual logo marquees */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 1 } : {}}
        transition={{ duration: 1.2 }}
        className="relative marquee-mask border-y border-white/10 bg-[var(--bg-2)]/50 py-6 md:py-8 space-y-4 md:space-y-6"
        data-testid="client-logo-marquee"
      >
        <LogoMarqueeRow items={rowA} direction="left" speed={33} />
        <LogoMarqueeRow items={rowB} direction="right" speed={27} />
      </motion.div>

      {/* Roster grid — all logos, uniform */}
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 mt-16 md:mt-24">
        <div className="grid grid-cols-12 gap-8 mb-8">
          <div className="col-span-12 md:col-span-3 text-[11px] tracking-[0.3em] uppercase text-[var(--ink-mute)]">
            The Roster · A to Z
          </div>
          <div className="col-span-12 md:col-span-6 md:col-start-4 text-[12px] tracking-[0.2em] uppercase text-[var(--ink-dim)]">
            {CLIENTS.length} brands on this page. Many more under NDA.
          </div>
        </div>
        <div
          className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-px bg-white/10 border border-white/10"
          data-testid="clients-grid"
        >
          {CLIENTS.map((c, i) =>
            c.logo ? <LogoTile key={c.name} c={c} i={i} /> : <NameTile key={c.name} c={c} i={i} />
          )}
        </div>
      </div>
    </section>
  );
}
